/** @format */

import { TOrgPerson } from "../../../../redux-modules/declarations/maps/organigramma";
import { fullNameOf, META_SEPARATOR, showsStaffPill } from "./people";

// Misura l'altezza delle card con nome o ruolo lunghi. Ogni card viene ricreata
// fuori schermo con le stesse classi di PersonNode, così la misura usa esattamente
// font, dimensioni e regole di a capo del CSS. Se cambi il markup di PersonNode,
// allinea anche probeOf.

const el = (tag: string, className: string, text?: string): HTMLElement => {
  const node = document.createElement(tag);
  node.className = className;
  if (text) node.textContent = text;
  return node;
};

/** Copia "muta" della card: stesso ingombro, nessun comportamento. */
const probeOf = (person: TOrgPerson, withActions: boolean): HTMLElement => {
  const card = el("div", `org-node org-node--probe${person.is_staff ? " org-node--staff" : ""}`);

  const avatar = el("span", "org-node__avatar");
  avatar.style.cssText = "display:inline-block;flex:none;width:38px;height:38px;";
  card.appendChild(avatar);

  const text = el("div", "org-node__text");
  const name = el("div", "org-node__name");
  name.appendChild(el("span", "org-node__name-text", fullNameOf(person)));
  if (person.is_coordinator) {
    const star = el("span", "anticon org-node__coordinator");
    star.style.cssText = "width:1em;height:1em;";
    name.appendChild(star);
  }
  text.appendChild(name);

  const meta = el("div", "org-node__meta");
  const metaParts: HTMLElement[] = [];
  if (person.role_name) metaParts.push(el("span", "org-node__role", person.role_name));
  if (showsStaffPill(person)) metaParts.push(el("span", "org-pill org-pill--staff", "Staff"));
  if (person.is_disabled) metaParts.push(el("span", "org-pill", "Disabilitato"));
  metaParts.forEach((part, i) => {
    if (i > 0) meta.appendChild(document.createTextNode(META_SEPARATOR));
    meta.appendChild(part);
  });
  text.appendChild(meta);
  card.appendChild(text);

  if (withActions) card.appendChild(el("span", "org-node__more"));
  return card;
};

/** Tutto ciò che cambia l'ingombro della card: card con la stessa firma hanno la stessa altezza. */
const signatureOf = (person: TOrgPerson, withActions: boolean): string =>
  [
    fullNameOf(person),
    person.role_name || "",
    person.is_staff ? 1 : 0,
    person.is_coordinator ? 1 : 0,
    person.is_disabled ? 1 : 0,
    withActions ? 1 : 0,
  ].join("\u0000");

/**
 * Altezza della card di ogni persona. `host` è un elemento dentro l'organigramma
 * (eredita font e variabili CSS); `cache` evita di rimisurare card già viste.
 */
export const measureCardHeights = (
  host: HTMLElement,
  people: TOrgPerson[],
  withActions: boolean,
  cache: Map<string, number>,
): Map<number, number> => {
  const signatures = people.map(p => signatureOf(p, withActions));

  const pending = new Map<string, TOrgPerson>();
  people.forEach((p, i) => {
    if (!cache.has(signatures[i]) && !pending.has(signatures[i])) pending.set(signatures[i], p);
  });

  if (pending.size > 0) {
    const container = document.createElement("div");
    container.setAttribute("aria-hidden", "true");
    container.style.cssText =
      "position:fixed;top:0;left:-100000px;visibility:hidden;pointer-events:none;contain:layout style;";

    const probes: [string, HTMLElement][] = [];
    pending.forEach((person, signature) => {
      const probe = probeOf(person, withActions);
      probes.push([signature, probe]);
      container.appendChild(probe);
    });

    host.appendChild(container);
    // Una sola lettura dopo aver inserito tutto: un unico ricalcolo del layout del browser
    probes.forEach(([signature, probe]) => {
      cache.set(signature, Math.ceil(probe.getBoundingClientRect().height));
    });
    host.removeChild(container);
  }

  const heights = new Map<number, number>();
  people.forEach((p, i) => heights.set(p.id, cache.get(signatures[i]) as number));
  return heights;
};
